const explicitBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
export const API_URL = explicitBase || window.location.origin;

export const storage = {
  get token() { return localStorage.getItem('fdn.token'); },
  set token(value: string | null) { value ? localStorage.setItem('fdn.token', value) : localStorage.removeItem('fdn.token'); },
  get deviceId() { return localStorage.getItem('fdn.deviceId'); },
  set deviceId(value: string | null) { value ? localStorage.setItem('fdn.deviceId', value) : localStorage.removeItem('fdn.deviceId'); },
  get installationId() {
    let value = localStorage.getItem('fdn.installationId');
    if (!value) {
      value = crypto.randomUUID();
      localStorage.setItem('fdn.installationId', value);
    }
    return value;
  },
  get trackingEnabled() { return localStorage.getItem('fdn.tracking') === 'true'; },
  set trackingEnabled(value: boolean) { localStorage.setItem('fdn.tracking', String(value)); }
};

/**
 * Carries the server's machine-readable failure code alongside the human
 * message so callers can branch on terminal states (an exhausted or consumed
 * OTP challenge) without matching on copy. `message` is unchanged.
 */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers || {});
  headers.set('Content-Type', 'application/json');
  // `/public/**` is the unauthenticated surface used by guardians, delivery
  // recipients and security client contacts, authorized by their own scoped
  // tracking token. Attaching a Family session token would hand a personal
  // credential to a link-shared page for no benefit: the server ignores it, and
  // the browser opening the link may not belong to the account at all.
  if (storage.token && !path.startsWith('/public/')) headers.set('Authorization', `Bearer ${storage.token}`);

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { ...init, headers });
  } catch {
    throw new ApiError('The server is unreachable. Check your connection and try again.', 0, 'OFFLINE');
  }
  const text = await response.text();
  let payload: any = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
  if (!response.ok) {
    const requestId = response.headers.get('x-request-id');
    const message = typeof payload?.error === 'string' ? payload.error : payload?.error ? JSON.stringify(payload.error) : `Request failed (${response.status})`;
    const code = typeof payload?.code === 'string' ? payload.code : undefined;
    if (response.status >= 500 && requestId) throw new ApiError(`${message} (reference ${requestId})`, response.status, code);
    throw new ApiError(message, response.status, code);
  }
  return payload as T;
}
